/**
 * POSTLAUNCH-V6 · Patch #2 (Fr 04.09.2026 vor Launch) — Compact Font-Scale in TopBar.
 *
 * Kompakter Minus-Plus-Toggle. Nutzt die gleiche `--fts-font-scale` CSS-Variable
 * + localStorage-Key `f4s_font_scale` wie <FontScaleToggle /> auf /profil.
 *
 * Sprint-V24 Issue J (11.02.2026, Boardroom-CoS): Reduziert auf 3 Stufen
 * [0.9, 1, 1.2] fuer weniger kognitiven Overhead.
 *
 * Sprint-V24 Nachschaerfung „Plan-Font-Kompakt" (11.02.2026): Auf 2 Stufen
 * [1, 1.25] reduziert (nie kleiner als 1). Anzeige nur noch Minus + Plus
 * Icons, kein „1x"-Label mehr. Mobile-First: sichtbar minimal.
 * localStorage-Migration: alte Werte 0.75/0.9 → 1, 1.2/1.25/1.5 → 1.25.
 */
import React, { useEffect, useState } from "react";
import { Minus, Plus } from "lucide-react";

const KEY = "f4s_font_scale";
const STEPS = [1, 1.25];

function nearestIndex(v) {
  const val = parseFloat(v) || 1;
  let best = 1, bestDiff = Infinity;
  STEPS.forEach((s, i) => {
    const d = Math.abs(s - val);
    if (d < bestDiff) { bestDiff = d; best = i; }
  });
  return best;
}

function apply(v) {
  try {
    document.documentElement.style.setProperty("--fts-font-scale", String(v));
    localStorage.setItem(KEY, String(v));
  } catch { /* ignore */ }
}

export default function TopBarFontScale() {
  const [idx, setIdx] = useState(() => {
    try { return nearestIndex(localStorage.getItem(KEY) || "1"); } catch { return 1; }
  });

  useEffect(() => { apply(STEPS[idx]); }, [idx]);

  const value = STEPS[idx];
  const atMin = idx <= 0;
  const atMax = idx >= STEPS.length - 1;

  return (
    <div
      className="flex items-center gap-0"
      data-testid="topbar-font-scale"
      role="group"
      aria-label="Schriftgröße"
    >
      <button
        type="button"
        onClick={() => !atMin && setIdx((i) => i - 1)}
        disabled={atMin}
        data-testid="topbar-font-scale-minus"
        aria-label="Schrift kleiner"
        title="Schrift kleiner"
        className="rounded text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-800 dark:hover:text-white disabled:opacity-30 disabled:cursor-not-allowed p-1 transition-colors"
      >
        <Minus className="w-3.5 h-3.5" />
      </button>
      {/* Sprint-V24 Nachschaerfung „Plan-Font-Kompakt" (11.02.2026):
          „1x"-Label entfernt. Nur noch Minus + Plus fuer minimale
          Mobile-Praesenz. `value` bleibt data-testid fuer Regression-Tests. */}
      <span data-testid="topbar-font-scale-value" data-value={value} className="sr-only">
        {value.toFixed(2).replace(/\.?0+$/, "")}x
      </span>
      <button
        type="button"
        onClick={() => !atMax && setIdx((i) => i + 1)}
        disabled={atMax}
        data-testid="topbar-font-scale-plus"
        aria-label="Schrift größer"
        title="Schrift größer"
        className="rounded text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-800 dark:hover:text-white disabled:opacity-30 disabled:cursor-not-allowed p-1 transition-colors"
      >
        <Plus className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}
